import { AllPublicationFolderE, PublicationFolderI, PublicationI, UserFileI } from './publication';
import { FolderSharePermissionE, FolderShareUserI } from './folder-share';

export const FOLDER_NAME_MAX_LENGTH = 255;
export const FOLDER_NOTES_MAX_LENGTH = 500;

export interface FolderI {
    id: number;
    name: string;
    notes?: string;
    // Folder notes are owner-only by default; the backend withholds them entirely until the owner shares them.
    notesShared?: boolean;
    uuidUser?: string;
    createdAt?: string;
    lastUpdateDate?: string;
    publicationList: Array<PublicationFolderI | UserFileI>;
    sharedUsers?: FolderShareUserI[];
    owner?: FolderShareUserI;
    owned?: boolean;
    // Undefined when owned is true; otherwise the permission (READ/EDIT) the current user was granted on this shared folder.
    currentUserPermission?: FolderSharePermissionE;
    // Whether the current user pinned this folder for quick access, whether they own it or it was shared with them.
    pinned?: boolean;
    chatShared?: boolean;
    projectId?: number;
    projectName?: string;
    documentReferences?: FolderPublicationReferenceI[];
    bookPartReferences?: FolderPublicationReferenceI[];
    // Books saved in their entirety in this folder, as opposed to bookPartReferences above which covers
    // individually saved chapters.
    bookReferences?: FolderPublicationReferenceI[];
}

export interface FolderPublicationReferenceI {
    id?: number;
    reference?: string;
    version?: number;
}

export interface FolderContentsI {
    publicationList: Array<PublicationFolderI | UserFileI>;
    bookReferences: FolderPublicationReferenceI[];
}

// GET .../folders endpoints never populate documentReferences/bookPartReferences server-side (only
// bookReferences, since whole books never appear in publicationList) - those two must be derived from
// publicationList on the client instead, whenever the endpoint response includes each publication's own
// details (id, typeCode).
export function hydrateFolderPublicationReferences(folder: FolderI): FolderI {
    return {
        ...folder,
        documentReferences: buildFolderPublicationReferences(folder.publicationList, false),
        bookPartReferences: buildFolderPublicationReferences(folder.publicationList, true),
        bookReferences: folder.bookReferences || []
    };
}

function buildFolderPublicationReferences(
    publicationList: Array<PublicationFolderI | UserFileI>,
    isBookPart: boolean
): FolderPublicationReferenceI[] {
    return (publicationList || [])
        .filter(item => (item as UserFileI)?.type !== AllPublicationFolderE.USER_FILE)
        .map(item => (item as PublicationFolderI).publication)
        .filter((publication): publication is PublicationI => !!publication)
        .filter(publication => (publication.typeCode === 'BOOK') === isBookPart)
        .map(publication => ({ id: publication.id }));
}

// What app-folder-card-selector needs to render one folder - satisfied by both a full FolderI and the lighter
// ProjectFolderI embedded in a ProjectI, so the same card can be used regardless of which one is on hand.
export interface FolderCardI {
    id: number;
    name: string;
    lastUpdateDate?: string;
    owner?: FolderShareUserI;
}

export enum FolderOwnershipFilterE {
    ALL = 'ALL',
    MINE = 'MINE',
    SHARED = 'SHARED'
}

export interface FolderFilterI {
    folderName?: string;
    ownership?: FolderOwnershipFilterE;
}

export enum FolderSortFieldE {
    NAME = 'name',
    LAST_UPDATE = 'lastUpdate'
}

export type FolderSortDirectionT = 'asc' | 'desc';

export interface FolderSelectionI {
    folderId?: number;
    folderName?: string;
    folderNotes?: string; // when it's a new folder
    // Set instead of folderId when the publication is saved directly at the project level, outside of any folder.
    projectId?: number;
}

export interface DocumentStoreInitI {
    folderList?: FolderI[];
}

export interface FolderNameAndNotesI {
    name: string;
    notes: string;
}

// Lightweight view of a folder the current user pinned for quick access, whether they own it or it was shared
// with them - carries only what a pinned-folders carousel needs to render, not the full folder payload.
export interface PinnedFolderI {
    id: number;
    name: string;
    owned: boolean;
    owner?: FolderShareUserI;
    lastUpdateDate?: string;
    // Undefined when owned is true; otherwise the permission (READ/EDIT) the current user was granted on this shared folder.
    currentUserPermission?: FolderSharePermissionE;
}
